// src/frontend/js/modern/core/conflict-manager.js
//
// Central conflict resolution engine.
//
// Core model: STRICT PER-SCOPE SERIALIZATION WITH CHAIN ANNIHILATION.
// Every operation on the same scope (product+list) is chained on a real
// promise queue, so the server receives the operations in exact click order
// and the final server state ALWAYS equals the last user intent.
//
// Rapid alternating clicks (e.g. add→remove→add→remove) are collapsed via
// chain annihilation: ALL pending opposite operations (both QUEUED and
// IN_FLIGHT) are cancelled when a new opposite arrives. The newest operation
// always executes with full visual feedback (coalesced = false), while the
// intermediate requests never reach the server. An in-flight request that was
// already sent is not aborted at the HTTP level; its response is used purely
// for identity extraction (item_id / wishlist_id) via onDataOnly.
//
// Optimistic UI is applied instantly per operation; each operation
// rolls back only itself on failure.
//
// VISUAL FEEDBACK DECOUPLING:
// The loading indicator and the optimistic mutation timer are started the
// moment the operation is enqueued (execute), NOT when the operation reaches
// its turn in the serialized chain (_runOperation). This guarantees instant
// user perception even while previous operations are still awaiting their
// server responses. The HTTP request itself remains strictly serialized so
// identity extraction keeps working.
import { eventBus, EVENTS } from './event-bus.js';
import { loading } from '../shared/ui-helpers.js';
import { validators } from '../shared/validators.js';
import { queueManager } from './queue.js';

// Operation state machine
const OP_STATE = {
	QUEUED: 'queued',           // Chained, not yet started
	IN_FLIGHT: 'in_flight',     // HTTP request sent
	CONFIRMED: 'confirmed',     // Server confirmed success
	ROLLED_BACK: 'rolled_back', // Rolled back due to error
	CANCELLED: 'cancelled',     // Coalesced away / superseded before sending
};

// Visual delay (ms) applied to optimistic side-effects (popup close,
// class toggles, row visibility, counter updates) so the loading
// indicator remains perceivable for its full minimum duration.
const OPTIMISTIC_UI_DELAY = validators.isTrue(window.wlfmc_l10n?.enable_ajax_loading) ? 500 : 0;

class ScopeTracker {
	constructor(scope) {
		this.scope = scope;
		this.operations = [];      // Ordered list of operations
		this.chain = Promise.resolve(); // Serialization chain
	}
	addOperation(op) {
		this.operations.push(op);
		// Keep the ledger small: confirmed/cancelled history beyond 20 entries.
		if (this.operations.length > 40) {
			this.operations = this.operations.filter(
				(o) => o.state === OP_STATE.QUEUED || o.state === OP_STATE.IN_FLIGHT
					|| this.operations.indexOf(o) >= this.operations.length - 20
			);
		}
	}
	getPendingOperations() {
		return this.operations.filter(
			(op) => op.state === OP_STATE.QUEUED || op.state === OP_STATE.IN_FLIGHT
		);
	}
}

class ConflictManager {
	constructor() {
		this._scopes = new Map();
		this._initialized = false;
		this._persistenceKey = 'wlfmc_pending_operations';
		// Per-scope identity store: holds item_id / wishlist_id / token
		// extracted from the most recent confirmed ADD response so that
		// a subsequent REMOVE can send correct identifiers to the server.
		this._identities = new Map();
	}

	init() {
		if (this._initialized) return;
		eventBus.on(EVENTS.ACTION_STARTED, (data) => {
			if (data.scope) this._onOperationStarted(data);
		});
		this._initialized = true;
		console.log('[ConflictManager] Initialized (serialized mode)');
	}

	getTracker(scope) {
		if (!this._scopes.has(scope)) {
			this._scopes.set(scope, new ScopeTracker(scope));
		}
		return this._scopes.get(scope);
	}

	/* ═══ Identity Store ═══ */

	/**
	 * Store server-confirmed identity for a scope.
	 * Called by module actions via onDataOnly when an ADD response
	 * arrives but a REMOVE is already pending.
	 */
	storeIdentity(scope, identity) {
		if (!identity) return;
		this._identities.set(scope, { ...identity, _ts: Date.now() });
	}

	/**
	 * Retrieve stored identity for a scope.
	 * Returns null if no identity has been stored.
	 */
	getIdentity(scope) {
		return this._identities.get(scope) || null;
	}

	/**
	 * Remove stored identity after it has been consumed.
	 */
	clearIdentity(scope) {
		this._identities.delete(scope);
	}

	/* ═══ Visual Feedback ═══ */

	/**
	 * Start visual feedback (loading indicator + optimistic mutation timer)
	 * immediately when the operation is enqueued, independent of its position
	 * in the serialization queue.
	 *
	 * For optimistic operations the loading helper runs for its minimum
	 * perceivable duration and the state mutation is applied after
	 * OPTIMISTIC_UI_DELAY. For non-optimistic operations the loading class
	 * persists until the server response releases it.
	 */
	_startVisualFeedback(operation, config) {
		const { optimisticFn, element, actionId } = config;

		if (optimisticFn) {
			const applyOptimistic = () => {
				try {
					optimisticFn();
				} catch (e) {
					console.error(`[ConflictManager] Optimistic apply failed for ${actionId}:`, e);
				}
			};
			// Purely visual loading indicator — fire-and-forget.
			if (element) {
				loading(element).catch(() => {});
			}
			operation._pendingOptimisticApply = applyOptimistic;
			operation._optimisticTimer = setTimeout(() => {
				if (operation._pendingOptimisticApply) {
					operation._pendingOptimisticApply();
					operation._pendingOptimisticApply = null;
				}
				operation._optimisticTimer = null;
			}, OPTIMISTIC_UI_DELAY);
		} else if (element) {
			const hasIcon = element.querySelector('i') !== null;
			element.classList.add('wlfmc-action', hasIcon ? 'wlfmc-loading' : 'wlfmc-loading-alt');
			// Stored on the operation so the response handler can release it.
			operation._releaseLoading = () => {
				element.classList.remove('wlfmc-loading', 'wlfmc-loading-alt');
			};
		}
	}

	/**
	 * Cancel a still-pending optimistic mutation WITHOUT applying it.
	 * Used when an operation is superseded before its optimistic delay elapses.
	 */
	_cancelPendingOptimistic(operation) {
		if (operation._optimisticTimer) {
			clearTimeout(operation._optimisticTimer);
			operation._optimisticTimer = null;
		}
		operation._pendingOptimisticApply = null;
	}

	/**
	 * Fully tear down visual feedback for an operation that will never run
	 * (coalesced while still QUEUED): cancel the pending optimistic mutation
	 * and release any persistent loading indicator.
	 */
	_cancelVisualFeedback(operation) {
		this._cancelPendingOptimistic(operation);
		if (operation._releaseLoading) {
			try { operation._releaseLoading(); } catch (e) { /* noop */ }
			operation._releaseLoading = null;
		}
	}

	/* ═══ Main Entry Point ═══ */

	/**
	 * Main entry point: Execute an operation with full conflict management.
	 */
	execute(config) {
		const { scope, type, productId, actionId } = config;
		const tracker = this.getTracker(scope);

		// ── Same-type deduplication ──────────────────────────────────
		// Rapid repeated clicks on the SAME action (e.g. clicking
		// "remove" 5 times) must NOT produce 5 server requests.
		// If an identical operation is already queued or in-flight,
		// return its existing promise so the caller awaits the same
		// single request.
		const pendingSame = tracker.operations.find(
			(op) => op.type === type
				&& (op.state === OP_STATE.QUEUED || op.state === OP_STATE.IN_FLIGHT)
				&& String(op.productId) === String(productId)
		);
		if (pendingSame && pendingSame._resultPromise) {
			return pendingSame._resultPromise;
		}

		const isRemoval = 'remove' === type || 'delete' === type;
		const oppositeTypes = isRemoval ? ['add'] : ['remove', 'delete'];

		// ── Chain Annihilation ───────────────────────────────────────
		// When rapid alternating clicks occur (add→remove→add→remove),
		// we must cancel ALL pending opposite operations, not just the
		// first one found. Otherwise intermediate operations coalesce
		// each other and the final operation gets stuck waiting for a
		// server response that will never drive the UI.
		//
		// Strategy:
		//  - QUEUED opposites: fully annihilate (cancel visuals + skip server).
		//  - IN_FLIGHT opposites: do NOT abort the HTTP request (its response
		//    is needed for identity extraction), but cancel all pending
		//    optimistic visual effects so they never drive the UI.
		//  - The NEWEST operation always runs with coalesced = false so its
		//    visual feedback starts immediately.
		const pendingOpposites = tracker.operations.filter(
			(op) => oppositeTypes.includes(op.type)
				&& (op.state === OP_STATE.QUEUED || op.state === OP_STATE.IN_FLIGHT)
				&& String(op.productId) === String(productId)
		);

		let coalesced = false;

		for (const pendingOpposite of pendingOpposites) {
			console.log(`[ConflictManager] Chain annihilation: ${pendingOpposite.state} ${pendingOpposite.type} cancelled by ${type}`);

			if (pendingOpposite.state === OP_STATE.IN_FLIGHT) {
				// Do NOT abort the in-flight request.
				// Its response is needed for identity extraction
				// (item_id / wishlist_id) by the subsequent opposite op.
				// Cancel only the pending optimistic visual effects.
				this._cancelPendingOptimistic(pendingOpposite);
			} else {
				// The opposite operation was only QUEUED (never left the browser).
				// Tear down its already-started visual feedback so its
				// optimistic mutation and loading indicator never surface.
				this._cancelVisualFeedback(pendingOpposite);
			}
			pendingOpposite.state = OP_STATE.CANCELLED;
		}

		// The newest operation ALWAYS executes with full visual feedback.
		// Intermediate requests were annihilated above and will never reach
		// the server, so only this final intent is serialized.
		coalesced = false;

		const operation = {
			actionId,
			type,
			module: config.module,
			productId,
			listId: config.listId,
			state: OP_STATE.QUEUED,
			timestamp: Date.now(),
		};

		let resolveResult;
		const resultPromise = new Promise((resolve) => {
			resolveResult = resolve;
		});
		operation._resolveResult = resolveResult;
		// Store the promise so same-type deduplication can return it
		// to subsequent identical clicks.
		operation._resultPromise = resultPromise;

		tracker.addOperation(operation);

		// Start visual feedback IMMEDIATELY so the user perceives an instant
		// response even while this operation waits behind previous ones in
		// the serialization chain. Since coalesced is always false for the
		// newest operation, visual feedback always starts here.
		this._startVisualFeedback(operation, config);

		eventBus.emit(EVENTS.ACTION_STARTED, { actionId, scope, type });

		const run = () => {
			this._runOperation(config, tracker, operation, coalesced);
			// Return the result promise to ensure the chain waits for this operation to settle
			return resultPromise;
		};
		tracker.chain = tracker.chain.then(run, run);
		return resultPromise;
	}

	/* ═══ Operation Runner ═══ */

	async _runOperation(config, tracker, operation, coalesced) {
		const {
			scope, actionId, type, productId, listId, module,
			serverFn, onSuccess, onError, optimisticFn, rollbackFn, element,
			onDataOnly
		} = config;

		if (coalesced) {
			operation.state = OP_STATE.CONFIRMED;
			operation.confirmedAt = Date.now();
			eventBus.emit(EVENTS.ACTION_COMPLETED, { actionId, scope, type, status: 'coalesced' });
			const inferredResult = { success: true, inferred: true, coalesced: true };
			if (onSuccess) onSuccess(inferredResult);
			operation._resolveResult?.(inferredResult);
			return;
		}

		operation.state = OP_STATE.IN_FLIGHT;

		/**
		 * Safely flush any pending optimistic mutation.
		 * Called from the server-response handlers to guarantee the store
		 * is in sync before CONFIRMED / ROLLED_BACK state is set.
		 */
		const flushOptimistic = () => {
			if (operation._optimisticTimer) {
				clearTimeout(operation._optimisticTimer);
				operation._optimisticTimer = null;
			}
			if (operation._pendingOptimisticApply) {
				operation._pendingOptimisticApply();
				operation._pendingOptimisticApply = null;
			}
		};

		/**
		 * Cancel a pending optimistic mutation WITHOUT applying it.
		 */
		const cancelOptimistic = () => {
			this._cancelPendingOptimistic(operation);
		};

		/**
		 * Release the persistent (non-optimistic) loading indicator, if any.
		 * Safe to call repeatedly.
		 */
		const releaseLoading = () => {
			if (operation._releaseLoading) {
				try { operation._releaseLoading(); } catch (e) { /* noop */ }
				operation._releaseLoading = null;
			}
		};

		try {
			// NOTE: Visual feedback (loading indicator + optimistic timer) was
			// already started at enqueue time in execute() for instant user
			// perception. Here we only fire the serialized server request and
			// reconcile the visual state with the response.
			const TIMEOUT = 30000;
			const serverPromise = Promise.race([
				serverFn(),
				new Promise((_, reject) =>
					setTimeout(() => reject(new Error('Operation timed out')), TIMEOUT)
				),
			]);

			serverPromise
				.then((result) => {
					operation.state = OP_STATE.CONFIRMED;
					operation.confirmedAt = Date.now();
					releaseLoading();

					// Determine whether a newer opposite operation is still
					// pending (queued or in-flight) for the same product.
					// If so, this response must NOT drive UI updates —
					// only internal data (identity) is extracted.
					const superseded = this._hasPendingOpposite(tracker, operation);
					if (superseded) {
						// Suppress all visual side-effects:
						// never apply the pending optimistic mutation.
						cancelOptimistic();
						// Invoke the data-only callback so the module can
						// persist item_id / wishlist_id for the upcoming
						// opposite operation without touching the UI.
						if (onDataOnly) {
							try {
								onDataOnly(result);
							} catch (e) {
								console.error(`[ConflictManager] onDataOnly failed for ${actionId}:`, e);
							}
						}
					} else {
						// Normal path: apply optimistic mutation then UI callback.
						flushOptimistic();
						if (onSuccess) onSuccess(result);
					}
					operation._resolveResult?.(result);
					eventBus.emit(EVENTS.ACTION_COMPLETED, {
						actionId, scope, type, result,
						status: superseded ? 'data_only' : 'success'
					});
				})
				.catch((error) => {
					if (error?.name === 'AbortError' || error?.message === 'Operation cancelled') {
						if (operation._optimisticTimer || operation._pendingOptimisticApply) {
							cancelOptimistic();
						} else if (rollbackFn) {
							try { rollbackFn(); } catch (e) {
								console.error('[ConflictManager] Rollback on abort failed:', e);
							}
						}
						releaseLoading();
						console.log(`[ConflictManager] Operation ${actionId} superseded/aborted.`);
						operation.state = OP_STATE.CANCELLED;
						operation._resolveResult?.({ success: false, superseded: true });
						eventBus.emit(EVENTS.ACTION_COMPLETED, {
							actionId, scope, type, status: 'cancelled'
						});
						return;
					}

					// For real errors, apply the optimistic mutation first
					// so rollbackFn can consistently undo it.
					flushOptimistic();
					releaseLoading();

					if (this._isNotFoundError(error)) {
						console.log(`[ConflictManager] "${error.message}" treated as already-settled`);
						operation.state = OP_STATE.CONFIRMED;
						operation.confirmedAt = Date.now();
						const inferredResult = { success: true, inferred: true };
						if (onSuccess) onSuccess(inferredResult);
						operation._resolveResult?.(inferredResult);
						return;
					}

					operation.state = OP_STATE.ROLLED_BACK;
					try {
						if (rollbackFn) rollbackFn();
					} catch (e) {
						console.error('[ConflictManager] Rollback failed:', e);
					}
					if (onError) onError(error);
					operation._resolveResult?.(null);
					eventBus.emit(EVENTS.ACTION_FAILED, {
						actionId, scope, type, error: error.message
					});
				});
			return;
		} catch (error) {
			cancelOptimistic();
			operation.state = OP_STATE.ROLLED_BACK;
			try { if (rollbackFn) rollbackFn(); } catch (e) { /* noop */ }
			releaseLoading();
			if (onError) onError(error);
			operation._resolveResult?.(null);
			eventBus.emit(EVENTS.ACTION_FAILED, { actionId, scope, type, error: error.message });
		}
	}

	/* ═══ Helpers ═══ */

	/**
	 * Check whether a pending (QUEUED or IN_FLIGHT) operation of the
	 * opposite type exists for the same product within this tracker.
	 * Used at response time to decide between full UI update and
	 * data-only extraction.
	 */
	_hasPendingOpposite(tracker, operation) {
		const removalTypes = ['remove', 'delete'];
		const addTypes = ['add', 'create'];
		const currentIsRemoval = removalTypes.includes(operation.type);
		const currentIsAdd = addTypes.includes(operation.type);
		return tracker.operations.some((op) => {
			if (op === operation) return false;
			if (op.state !== OP_STATE.QUEUED && op.state !== OP_STATE.IN_FLIGHT) return false;
			if (String(op.productId) !== String(operation.productId)) return false;
			const opIsRemoval = removalTypes.includes(op.type);
			const opIsAdd = addTypes.includes(op.type);
			return (currentIsRemoval && opIsAdd) || (currentIsAdd && opIsRemoval);
		});
	}

	_isNotFoundError(error) {
		const msg = (error.message || '').toLowerCase();
		return (
			msg.includes('not found') ||
			msg.includes('does not exist') ||
			msg.includes('no item') ||
			msg.includes('item not') ||
			msg.includes('product not') ||
			error.status === 404
		);
	}

	_onOperationStarted() { /* reserved for instrumentation */ }

	getPendingOperations() {
		const pending = [];
		for (const [scope, tracker] of this._scopes) {
			const ops = tracker.getPendingOperations();
			if (ops.length > 0) {
				pending.push({
					scope,
					operations: ops.map((op) => ({
						actionId: op.actionId,
						type: op.type,
						module: op.module,
						productId: op.productId,
						listId: op.listId,
						state: op.state,
						timestamp: op.timestamp,
					}))
				});
			}
		}
		return pending;
	}

	/**
	 * Get ALL operations (including CONFIRMED) within the tracker's memory window.
	 * Used by StateCache to protect recently confirmed items from stale server syncs.
	 */
	getOperations() {
		const all = [];
		for (const [scope, tracker] of this._scopes) {
			if (tracker.operations.length > 0) {
				all.push({
					scope,
					operations: tracker.operations.map((op) => ({
						actionId: op.actionId,
						type: op.type,
						module: op.module,
						productId: op.productId,
						listId: op.listId,
						state: op.state,
						timestamp: op.timestamp,
						confirmedAt: op.confirmedAt,
					}))
				});
			}
		}
		return all;
	}

	persistPendingOperations() {
		const pending = this.getPendingOperations();
		if (pending.length > 0) {
			try {
				sessionStorage.setItem(this._persistenceKey, JSON.stringify(pending));
				console.log(`[ConflictManager] Persisted ${pending.length} pending operation groups`);
			} catch (e) {
				console.warn('[ConflictManager] Failed to persist operations:', e);
			}
		}
	}

	clear() {
		this._scopes.clear();
		this._identities.clear();
	}

	destroy() {
		this._scopes.clear();
		this._identities.clear();
		this._initialized = false;
	}
}

export const conflictManager = new ConflictManager();
export default conflictManager;
export { OP_STATE };
